import * as z from 'zod'
import {
  isDialOutAgent,
  DialOutIntent,
  DIAL_OUT_INTENT_DEFAULT,
  AGENT_TONE_VALUES,
  AGENT_CHANNEL_VALUES,
  BUILT_IN_TOOL_IDS,
  STAFF_CONNECTION_METHOD_VALUES,
  OUTBOUND_TRIGGER_TYPE_VALUES,
  INBOUND_TRIGGER_TYPE_VALUES,
  INTAKE_FORM_SLOT_KEYS,
  INTAKE_SLOT_POLICY_VALUES,
  VOICE_SETTINGS_DEFAULTS,
  VOICE_OPTIONS,
  LEAD_RECEIVED_EVENT_NAME,
  WEBHOOK_EVENT_TO_TRIGGER,
  WEBHOOK_TRIGGER_TO_EVENT,
  type AgentChannel,
  type AgentDirection,
  type AgentTemplate,
  type CallFlowConfig,
  type TriggerType,
  type CreateAgentTriggerDto,
  type AgentWithVoiceSettings,
  type UpsertDialOutSettingsDto,
} from '@super/shared-types'
export const DEFAULT_INTAKE_SLOTS = [
  {
    slot: 'contact.name' as const,
    onCrm: 'skip' as const,
    onHistory: 'confirm' as const,
  },
  {
    slot: 'contact.email' as const,
    onCrm: 'skip' as const,
    onHistory: 'confirm' as const,
  },
  {
    slot: 'contact.property' as const,
    onCrm: 'confirm' as const,
    onHistory: 'ask' as const,
  },
  {
    slot: 'contact.group' as const,
    onCrm: 'skip' as const,
    onHistory: 'ask' as const,
  },
]

const formObjectSchema = z.object({
  name: z.string().min(1, 'Agent name is required'),
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  channels: z
    .array(z.enum(AGENT_CHANNEL_VALUES))
    .min(1, 'Select at least one channel'),
  disallowedTopics: z.string().optional(),
  greeting: z.string().optional(),
  prompt: z.string().optional(),
  transferInstructions: z.string().optional(),
  tone: z.array(z.enum(AGENT_TONE_VALUES)).optional(),
  voiceModelId: z.string().optional(),
  enableTypingInBackground: z.boolean().optional(),
  smsFollowUpText: z.string().optional(),
  isSmsFollowUpEnabled: z.boolean().optional(),
  declinedMessage: z.string().optional(),
  staffConnectionMethod: z
    .enum(STAFF_CONNECTION_METHOD_VALUES)
    .optional()
    .nullable(),
  preserveOriginalCallerId: z.boolean().optional(),
  triggerType: z
    .enum(OUTBOUND_TRIGGER_TYPE_VALUES)
    .or(z.enum(INBOUND_TRIGGER_TYPE_VALUES))
    .optional(),
  scenarioId: z.string().optional().nullable(),
  // Custom Trigger selection (outbound). When set, takes precedence over the
  // built-in triggerType enum — the agent links to this Trigger by id+eventName.
  triggerId: z.string().optional().nullable(),
  triggerEventName: z.string().optional().nullable(),
  knowledgeBaseIds: z.array(z.string()).optional(),
  workflowIds: z.array(z.string()).optional(),
  emailSubject: z.string().max(100).optional(),
  voicemailMessage: z.string().optional(),
  leaveVoicemailEnabled: z.boolean().optional(),
  dialOutSettings: z
    .object({
      mode: z.enum(['ACTION_LIST', 'TRIGGER_VARIABLE']).nullable().optional(),
      actionGroupIds: z.array(z.string()).optional(),
      variableName: z.string().nullable().optional(),
      intent: z.nativeEnum(DialOutIntent).optional(),
    })
    .optional(),
  enabledTools: z.array(z.enum(BUILT_IN_TOOL_IDS)).optional(),
  intakeForm: z
    .object({
      enabled: z.boolean(),
      // Optional so an off/untouched form always validates — the disabled
      // state must be saveable without ever configuring a slot.
      // Prompts accept null to match the API contract (IntakeFormSlot),
      // so stored configs can never brick the save.
      slots: z
        .array(
          z.object({
            slot: z.enum(INTAKE_FORM_SLOT_KEYS),
            onCrm: z.enum(INTAKE_SLOT_POLICY_VALUES),
            onHistory: z.enum(INTAKE_SLOT_POLICY_VALUES),
            confirmPrompt: z.string().nullable().optional(),
            askPrompt: z.string().nullable().optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  skills: z
    .array(
      z.object({
        type: z.string(),
        params: z.record(z.string(), z.unknown()).nullable().optional(),
      }),
    )
    .optional(),
})

// Dial-out settings only apply to outbound CALL agents; stale values left over
// from a channel switch must not block saving (the hidden field would fail
// validation with no visible error).
export const formSchema = formObjectSchema.superRefine((values, ctx) => {
  const settings = values.dialOutSettings
  if (!settings?.mode) return
  if (
    !isDialOutAgent({ direction: values.direction, channels: values.channels })
  )
    return
  if (settings.mode === 'ACTION_LIST') {
    if (!settings.actionGroupIds || settings.actionGroupIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['dialOutSettings', 'actionGroupIds'],
        message: 'Pick at least one action group',
      })
    }
  } else if (settings.mode === 'TRIGGER_VARIABLE') {
    if (!settings.variableName) {
      ctx.addIssue({
        code: 'custom',
        path: ['dialOutSettings', 'variableName'],
        message: 'Choose a variable as a dial out phone number source',
      })
    }
  }
})

export type FormValues = z.infer<typeof formSchema>

/**
 * Map a stored intakeFormConfig to form values. Always returns the complete
 * shape — resetting the form with a partial/undefined intakeForm bricks the
 * save with no visible error once the card mounts.
 */
export function intakeFormValuesFromConfig(
  config: CallFlowConfig | null | undefined,
): NonNullable<FormValues['intakeForm']> {
  // Length check, not truthiness: an empty slot list does nothing at runtime,
  // so it loads as disabled rather than showing the toggle on.
  return config?.intakeForm?.length
    ? { enabled: true, slots: config.intakeForm }
    : { enabled: false, slots: DEFAULT_INTAKE_SLOTS }
}

type NormalizedTrigger = {
  triggerType: TriggerType | undefined
  agentTrigger: CreateAgentTriggerDto | null
}

function normalizeInboundTrigger(
  values: Pick<FormValues, 'triggerType' | 'scenarioId'>,
): NormalizedTrigger {
  if (values.triggerType === 'SCENARIO') {
    return {
      triggerType: 'SCENARIO',
      agentTrigger: {
        type: 'SCENARIO',
        scenarioId: values.scenarioId ?? undefined,
      },
    }
  }
  return { triggerType: 'INBOUND_CALL', agentTrigger: null }
}

function normalizeOutboundTrigger(
  values: Pick<
    FormValues,
    'triggerType' | 'scenarioId' | 'triggerId' | 'triggerEventName'
  >,
): NormalizedTrigger {
  if (values.triggerType === 'SCENARIO') {
    return {
      triggerType: 'SCENARIO',
      agentTrigger: {
        type: 'SCENARIO',
        scenarioId: values.scenarioId ?? undefined,
      },
    }
  }
  // Custom Trigger: link by id + eventName. No built-in triggerType enum value
  // applies, so voiceSettings.triggerType stays unset.
  if (values.triggerId && values.triggerEventName) {
    return {
      triggerType: undefined,
      agentTrigger: {
        type: 'WEBHOOK',
        eventName: values.triggerEventName,
        triggerId: values.triggerId,
      },
    }
  }
  const eventName =
    (values.triggerType && WEBHOOK_TRIGGER_TO_EVENT[values.triggerType]) ||
    LEAD_RECEIVED_EVENT_NAME
  const triggerType = WEBHOOK_EVENT_TO_TRIGGER[eventName] ?? 'LEAD_WEBHOOK'
  return {
    triggerType,
    agentTrigger: { type: 'WEBHOOK', eventName },
  }
}

/**
 * Map form trigger values to API-compatible types.
 * Direction-aware: delegates to inbound/outbound-specific normalization.
 */
export function normalizeTriggerForApi(
  values: Pick<
    FormValues,
    'triggerType' | 'scenarioId' | 'triggerId' | 'triggerEventName'
  >,
  direction: AgentDirection,
): NormalizedTrigger {
  return direction === 'INBOUND'
    ? normalizeInboundTrigger(values)
    : normalizeOutboundTrigger(values)
}

/**
 * Derive the form triggerType from agent data.
 * Inverse of normalizeTriggerForApi (agent → form, not form → API).
 */
export function resolveFormTriggerType(
  agent: AgentWithVoiceSettings,
): FormValues['triggerType'] {
  const trigger = agent.agentTriggers?.[0]
  if (trigger?.type === 'WEBHOOK') {
    // Custom Trigger — selection is driven by triggerId, not the built-in enum.
    if (trigger.triggerId) return undefined
    const mapped = trigger.eventName
      ? WEBHOOK_EVENT_TO_TRIGGER[trigger.eventName]
      : undefined
    return mapped ?? 'LEAD_WEBHOOK'
  }
  if (trigger?.type === 'SCENARIO') return 'SCENARIO'
  if (agent.direction === 'INBOUND') return 'INBOUND_CALL'
  return agent.voiceSettings?.triggerType ?? undefined
}

const TRIGGER_LABELS: Record<string, string> = {
  INBOUND_CALL: 'Receptionist',
  SCENARIO: 'Scenario',
  WEBHOOK: 'Webhook',
  CRON: 'Scheduled',
}

/** Format agent trigger for display in tables/lists.
 *  Pass scenarioNames map to resolve scenario display names from IDs. */
export function formatTriggerLabel(
  agent: AgentWithVoiceSettings,
  scenarioNames?: Record<string, string>,
): string {
  const trigger = agent.agentTriggers?.[0]
  if (!trigger) return TRIGGER_LABELS.INBOUND_CALL
  const label = TRIGGER_LABELS[trigger.type] ?? trigger.type
  const name =
    trigger.name ?? (trigger.scenarioId && scenarioNames?.[trigger.scenarioId])
  return name ? `${label} | ${name}` : label
}

/**
 * Build the UpsertDialOutSettingsDto payload from form state.
 * Returns null when the agent can't dial out (not outbound or no CALL channel)
 * OR when the form has no mode selected / invalid state for the chosen mode.
 * API discriminatedUnion rejects invalid shapes, so this only emits valid variants.
 */
export function buildDialOutSettingsPayload(
  dialOutSettings: FormValues['dialOutSettings'],
  direction: AgentDirection,
  channels: AgentChannel[],
): UpsertDialOutSettingsDto | null {
  if (!isDialOutAgent({ direction, channels })) return null
  if (!dialOutSettings?.mode) return null

  if (dialOutSettings.mode === 'ACTION_LIST') {
    if (
      !dialOutSettings.actionGroupIds ||
      dialOutSettings.actionGroupIds.length === 0
    )
      return null
    return {
      mode: 'ACTION_LIST',
      actionGroupIds: dialOutSettings.actionGroupIds,
      intent: dialOutSettings.intent,
    }
  }

  // TRIGGER_VARIABLE
  if (!dialOutSettings.variableName) return null
  return {
    mode: 'TRIGGER_VARIABLE',
    variableName: dialOutSettings.variableName,
    intent: dialOutSettings.intent,
  }
}

export function getDefaultValues(
  direction: AgentDirection,
  template?: AgentTemplate,
): FormValues {
  const defaultVoice = VOICE_OPTIONS[0]

  if (template) {
    const templateChannels = template.channels ?? ['CALL', 'SMS', 'EMAIL']

    return {
      name: template.name,
      direction,
      channels: templateChannels,
      disallowedTopics:
        template.defaultSettings.disallowedTopics ??
        VOICE_SETTINGS_DEFAULTS.disallowedTopics ??
        '',
      greeting: template.defaultSettings.greeting || '',
      prompt: template.defaultSettings.prompt || '',
      transferInstructions: template.defaultSettings.transferInstructions || '',
      tone: template.defaultSettings.tone || ['friendly'],
      voiceModelId: template.defaultSettings.voiceModelId ?? defaultVoice.id,
      enableTypingInBackground:
        VOICE_SETTINGS_DEFAULTS.enableTypingInBackground,
      smsFollowUpText: template.defaultSettings.smsFollowUpText || '',
      // Inbound agents keep today's always-on post-call SMS by default,
      // matching agent-dao.ts's create default; outbound stays opt-in.
      isSmsFollowUpEnabled:
        template.defaultSettings.isSmsFollowUpEnabled ??
        direction === 'INBOUND',
      declinedMessage: '',
      staffConnectionMethod:
        template.defaultSettings.staffConnectionMethod ?? undefined,
      preserveOriginalCallerId: false,
      triggerType: undefined,
      scenarioId: null,
      knowledgeBaseIds: [],
      workflowIds: [],
      emailSubject: template.defaultSettings.emailSubject || '',
      voicemailMessage: '',
      leaveVoicemailEnabled: true,
      dialOutSettings: {
        mode: isDialOutAgent({ direction, channels: templateChannels })
          ? 'ACTION_LIST'
          : null,
        actionGroupIds: [],
        variableName: null,
        intent: DIAL_OUT_INTENT_DEFAULT,
      },
      enabledTools: [],
      intakeForm: {
        enabled: false,
        slots: DEFAULT_INTAKE_SLOTS,
      },
      skills: [],
    }
  }

  return {
    name: '',
    direction,
    channels: ['CALL'],
    disallowedTopics: VOICE_SETTINGS_DEFAULTS.disallowedTopics ?? '',
    greeting: '',
    prompt: '',
    transferInstructions: '',
    tone: ['friendly'],
    voiceModelId: defaultVoice.id,
    enableTypingInBackground: VOICE_SETTINGS_DEFAULTS.enableTypingInBackground,
    smsFollowUpText: '',
    isSmsFollowUpEnabled: direction === 'INBOUND',
    declinedMessage: '',
    staffConnectionMethod: undefined,
    preserveOriginalCallerId: false,
    triggerType: undefined,
    scenarioId: null,
    knowledgeBaseIds: [],
    workflowIds: [],
    emailSubject: '',
    voicemailMessage: '',
    leaveVoicemailEnabled: true,
    dialOutSettings: {
      mode: isDialOutAgent({ direction, channels: ['CALL'] })
        ? 'ACTION_LIST'
        : null,
      actionGroupIds: [],
      variableName: null,
      intent: DIAL_OUT_INTENT_DEFAULT,
    },
    enabledTools: [],
    intakeForm: {
      enabled: false,
      slots: DEFAULT_INTAKE_SLOTS,
    },
    skills: [],
  }
}
